import * as Sentry from "@sentry/react";
import { ClerkProvider } from "@clerk/clerk-react";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import { initSentry } from "@/lib/sentry";
import { runtimeEnv } from "@/lib/runtimeEnv";
import { APP_THEME_CONFIG, ThemeProvider } from "@/components/ThemeProvider";
import { migrateStoredTheme } from "@/lib/storedThemeMigration";
import { AppErrorFallback } from "@/components/FullPageNotice";
import App from "./App";

initSentry();

const THEME_MIGRATION_KEY = "fomo-fi-theme-migration-v1";
if (typeof window !== "undefined" && !localStorage.getItem(THEME_MIGRATION_KEY)) {
  localStorage.removeItem("fomo-fi-theme");
  localStorage.setItem(THEME_MIGRATION_KEY, "done");
}
// Theme option B (BEN-141): "System" is gone. A stored "system" becomes what the device
// shows now; nothing stored becomes light. Before the first render, so frame one is right.
if (typeof window !== "undefined") {
  migrateStoredTheme(
    localStorage,
    APP_THEME_CONFIG.storageKey,
    window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false,
  );
}

const CLERK_PUBLISHABLE_KEY = runtimeEnv.clerkPublishableKey();

if (!CLERK_PUBLISHABLE_KEY) {
  throw new Error(
    "Missing Clerk publishable key: set APP_CLERK_PUBLISHABLE_KEY on the container or VITE_CLERK_PUBLISHABLE_KEY in .env",
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ThemeProvider {...APP_THEME_CONFIG}>
      <Sentry.ErrorBoundary fallback={<AppErrorFallback />}>
        <ClerkProvider
          publishableKey={CLERK_PUBLISHABLE_KEY}
          signInUrl="/auth/login"
          signUpUrl="/auth/signup"
          afterSignOutUrl="/"
          // FP-174: a Clerk request that never reached the server must THROW
          // ("network_error"), not resolve and leave the reused SignIn object unchanged:
          // a swallowed retry after an earlier attempt kept that attempt's id, so nothing
          // could tell it from success (mobile, Android, R2). It also makes an offline
          // sign-out say it failed; before, it looked done and the session came back
          // (mobile, D5).
          experimental={{ rethrowOfflineNetworkErrors: true }}
        >
          <App />
        </ClerkProvider>
      </Sentry.ErrorBoundary>
    </ThemeProvider>
  </StrictMode>,
);
